import { CommonModule, Location } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  Signal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CardComponent } from '@app/components/card';
import { FormActionButtonComponent } from '@app/components/form-action-button';
import {
  InputComponent,
  InputDateRestriction,
  InputFieldSpacing,
  InputType,
  InputVariant,
  InputWidth,
} from '@app/components/input';
import { SpinnerComponent } from '@app/components/spinner';
import {
  TextColor,
  TextComponent,
  TextHeight,
  TextSize,
  TextSpacing,
  TextWeight,
} from '@app/components/text';
import {
  TitleComponent,
  TitleSize,
  TitleSpacing,
} from '@app/components/title-section';
import { ToastService, ToastType } from '@app/components/toast';
import {
  ToggleComponent,
  ToggleHexColor,
  ToggleSize,
} from '@app/components/toggle';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ChallengeBody,
  ChallengeBodyTeam,
  GroupChallengeTeamAssignmentStrategy,
} from '@skyfit/core-data-library';

import { AdminChallengeTeamsService } from '@app/services/admin-challenge-teams/admin-challenge-teams.service';
import { AdminChallengesV2Service } from '@app/services/admin-challenges-v2/admin-challenges-v2.service';
import { AdminChallengesService } from '@app/services/admin-challenges/admin-challenges.service';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { Logger } from '@app/services/logger/logger.service';
import { SaveChangesService } from '@app/services/save-changes/save-changes.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { combineLatest, of, switchMap } from 'rxjs';
import { SaveChangesConfirmationComponent } from '../../../shared/components/save-changes-confirmation/save-changes-confirmation.component';
import { GroupChallengeRoute } from '../challenges-route';
import { ChallengeTeamsComponent } from './challenge-teams/challenge-teams.component';

export enum CreateMode {
  EDIT = 'edit',
  CREATE = 'create',
  DUPLICATE = 'duplicate',
}

@Component({
  selector: 'app-challenge-create',
  templateUrl: './challenge-create.component.html',
  styleUrls: ['./challenge-create.component.css'],
  standalone: true,
  imports: [
    CardComponent,
    CommonModule,
    FormActionButtonComponent,
    InputComponent,
    ReactiveFormsModule,
    SpinnerComponent,
    TextComponent,
    TitleComponent,
    ToggleComponent,
    TranslatePipe,
    SaveChangesConfirmationComponent,
    ChallengeTeamsComponent,
  ],
})
export class ChallengeCreateComponent implements OnInit {
  private readonly translatorService = inject(TranslatorService);
  private readonly challengesService = inject(AdminChallengesService);
  private readonly challengesServicev2 = inject(AdminChallengesV2Service);
  private readonly tenantService = inject(AdminTenantService);
  private readonly messageService = inject(ToastService);
  private readonly fb = inject(FormBuilder);
  private readonly location = inject(Location);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly saveChanges = inject(SaveChangesService);
  private readonly teamsService = inject(AdminChallengeTeamsService);
  private readonly logger = inject(Logger);

  challenge = signal<ChallengeBody>(undefined);
  challengeTitle = signal<string>(undefined);
  inputDateRestriction = InputDateRestriction;
  isSaving = signal<boolean>(false);
  isLoading = signal(true);
  backTitle = signal('');
  errorMessages = signal({
    title: '',
    description: '',
    signupDate: '',
    startDate: '',
    allowAutoJoin: '',
    numberOfTeams: '',
    maxTeamNumber: '',
  });
  challengeWeeks: Signal<string> = computed(() =>
    this.calculateWeeks(this.challenge()?.durationInDays)
  );
  mode = signal<CreateMode>(CreateMode.CREATE);
  teamAssignmentStrategy = signal(GroupChallengeTeamAssignmentStrategy.DEFAULT);
  readOnlyMode: boolean;
  checked = false;
  descriptionError: boolean;
  form: FormGroup;
  signupDateError: boolean;
  startDateError = false;
  startDateErrorMessage: string;

  // TODO: we need to start cleaning all the interfaces and enums up. They suck!
  backTo: GroupChallengeRoute;
  ToggleHexColor = ToggleHexColor;
  TitleSize = TitleSize;
  TitleSpacing = TitleSpacing;
  InputVariant = InputVariant;
  InputWidth = InputWidth;
  InputFieldSpacing = InputFieldSpacing;
  InputType = InputType;
  ToggleSize = ToggleSize;
  TextColor = TextColor;
  TextSize = TextSize;
  TextSpacing = TextSpacing;
  TextWeight = TextWeight;
  TextHeight = TextHeight;
  groupChallengeTeamAssignmentStrategy = GroupChallengeTeamAssignmentStrategy;

  readonly teamAssignmentOptions = [
    {
      strategy: GroupChallengeTeamAssignmentStrategy.DEFAULT,
      label: 'groupChallenges.defaultTeamAssignmentStrategy',
    },
    {
      strategy: GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_COUNT,
      label: 'groupChallenges.fixCountTeamAssignmentStrategy',
    },
    {
      strategy: GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_SIZE,
      label: 'groupChallenges.maxTeamAssignmentStrategy',
    },
    {
      strategy: GroupChallengeTeamAssignmentStrategy.MANUAL,
      label: 'groupChallenges.manualTeamAssignmentStrategy',
    },
  ];

  private readonly requiredErrorSetter = (prop: string) => {
    this.errorMessages.update((value) => ({
      ...value,
      [prop]: this.translatorService.translate('common.requiredField'),
    }));
  };

  ngOnInit(): void {
    this.form = this.fb.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      signupDate: [
        null,
        [
          this.checkIfDateIsPast(),
          this.checkSignupDate(),
          this.checkIfRequire(
            () =>
              this.teamAssignmentStrategy() ===
              GroupChallengeTeamAssignmentStrategy.MANUAL
          ),
        ],
      ],
      startDate: [
        null,
        [
          this.checkIfDateIsPast(),
          this.checkIfDateIsToday(),
          Validators.required,
        ],
      ],
      numberOfTeams: [
        '',
        [
          this.checkIfRequire(
            () =>
              this.teamAssignmentStrategy() ===
                GroupChallengeTeamAssignmentStrategy.DEFAULT ||
              this.teamAssignmentStrategy() ===
                GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_SIZE ||
              this.teamAssignmentStrategy() ===
                GroupChallengeTeamAssignmentStrategy.MANUAL
          ),
          Validators.min(2),
          Validators.max(10),
        ],
      ],
      maxTeamNumber: [
        '',
        [
          this.checkIfRequire(
            () =>
              this.teamAssignmentStrategy() ===
                GroupChallengeTeamAssignmentStrategy.DEFAULT ||
              this.teamAssignmentStrategy() ===
                GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_COUNT ||
              this.teamAssignmentStrategy() ===
                GroupChallengeTeamAssignmentStrategy.MANUAL
          ),
          Validators.min(3),
        ],
      ],
      endDate: [''],
      allowAutoJoin: [false],
    });

    combineLatest([this.route.params, this.route.data, this.route.queryParams])
      .pipe(
        switchMap(([params, data, query]) => {
          if (data.mode) {
            this.mode.set(data.mode);
          } else {
            this.logger.error(
              'ChallengeCreateComponent: Route data is missing a `mode`. Defaulting to CREATE. Please check the routing configuration.'
            );
          }
          this.readOnlyMode = this.mode() === CreateMode.EDIT;
          this.setBackTitle(query.from);
          this.backTo = query.from;
          if (params.id) {
            const id = this.challengesService.decodeChallengeId(params.id);
            return this.challengesServicev2.getChallenge(id);
          }
          return of(undefined);
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((challenge) => {
        if (challenge) {
          const tenantId = this.tenantService.getTenantId();
          if (
            !this.challengesService.canTenantAccessChallenge(
              challenge,
              tenantId
            )
          ) {
            this.router.navigate(['challenges']);
            return;
          }
          if (this.mode() !== CreateMode.EDIT) {
            challenge.teams = [];
          }
          if (this.mode() === CreateMode.CREATE) {
            challenge.teamAssignmentStrategy =
              GroupChallengeTeamAssignmentStrategy.DEFAULT;
            challenge.teamAssignmentStrategyValue = null;
          }
          this.form.patchValue({
            title:
              this.mode() === CreateMode.DUPLICATE
                ? challenge.title + ' DUPLICATE'
                : challenge.title,
            description: challenge.description,
            allowAutoJoin: challenge.allowAutoJoin,
          });
          this.challenge.set(challenge);
          this.challengeTitle.set(challenge.title);
          if (this.mode() === CreateMode.EDIT) {
            this.checked = challenge.allowAutoJoin;
            this.form.patchValue({
              title: challenge.title,
              description: challenge.description,
              signupDate:
                challenge.teamAssignmentStrategy ===
                GroupChallengeTeamAssignmentStrategy.MANUAL
                  ? null
                  : this.toDateString(challenge.signupDate),
              startDate: this.toDateString(challenge.startDate),
              allowAutoJoin: challenge.allowAutoJoin,
            });
          }
          if (
            challenge.teamAssignmentStrategy ===
            GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_COUNT
          ) {
            this.teamAssignmentStrategy.set(
              GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_COUNT
            );
            this.form.patchValue({
              numberOfTeams: challenge.teamAssignmentStrategyValue,
            });
          } else if (
            challenge.teamAssignmentStrategy ===
            GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_SIZE
          ) {
            this.teamAssignmentStrategy.set(
              GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_SIZE
            );
            this.form.patchValue({
              maxTeamNumber: challenge.teamAssignmentStrategyValue,
            });
          } else if (
            challenge.teamAssignmentStrategy ===
            GroupChallengeTeamAssignmentStrategy.MANUAL
          ) {
            this.teamAssignmentStrategy.set(
              GroupChallengeTeamAssignmentStrategy.MANUAL
            );
          } else {
            this.teamAssignmentStrategy.set(
              GroupChallengeTeamAssignmentStrategy.DEFAULT
            );
          }
        }
        this.isLoading.set(false);
      });

    const fieldErrorMap = {
      signupDate: this.updateSignupDateErrors.bind(this),
      title: this.updateTitleErrors.bind(this),
      description: this.updateDescriptionErrors.bind(this),
      startDate: this.updateStartDateErrors.bind(this),
      numberOfTeams: this.updateNumberOfTeamsErrors.bind(this),
      maxTeamNumber: this.updateMaxNumberOfTeamsErrors.bind(this),
    };

    Object.keys(fieldErrorMap).forEach((field) => {
      this.form
        .get(field)
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          fieldErrorMap[field]();
        });
    });

    this.form
      .get('startDate')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((startDate) => {
        if (startDate) {
          const endDate = this.calculateEndDate(startDate);
          this.form.patchValue({
            endDate: this.toDateString(endDate),
          });
        }

        this.form.controls.signupDate.updateValueAndValidity();
      });

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.saveChanges.setHasChanges(this.hasChallengeChanges());
      });
  }

  sendToastMessage(message: string, type: ToastType) {
    this.messageService.add(message, type);
  }

  handleCheck(): void {
    this.checked = !this.checked;
    this.form.patchValue({ allowAutoJoin: this.checked });
  }

  onSave(): void {
    this.isSaving.set(true);
    if (!this.form.valid) {
      this.updateStartDateErrors();
      this.updateSignupDateErrors();
      this.updateTitleErrors();
      this.updateDescriptionErrors();
      this.updateNumberOfTeamsErrors();
      this.updateMaxNumberOfTeamsErrors();
      this.isSaving.set(false);
    } else {
      if (this.mode() === CreateMode.EDIT) {
        this.updateChallenge();
      } else if (
        this.mode() === CreateMode.CREATE ||
        this.mode() === CreateMode.DUPLICATE
      ) {
        this.createChallenge();
      }
    }
  }

  clearSpinner() {
    this.isSaving.set(false);
  }

  goBack(): void {
    if (this.backTo === GroupChallengeRoute.challenges) {
      this.router.navigate(['challenges']);
    } else {
      this.location.back();
    }
  }

  get goBackNavigate(): string[] {
    if (this.backTo === GroupChallengeRoute.challenges) {
      return ['challenges'];
    }
    return undefined;
  }

  onSelectEdit(): void {
    this.readOnlyMode = false;
  }

  getAssignmentDataTestId(
    strategy: GroupChallengeTeamAssignmentStrategy
  ): string {
    return `group-challenges-team-assignment-option-${strategy}-toggle`;
  }

  private calculateWeeks(durationInDays?: number): string {
    return durationInDays ? (durationInDays / 7).toFixed(0) : '';
  }

  private checkIfDateIsPast(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null || this.mode() === CreateMode.EDIT) {
        return null;
      }
      const inputDate = new Date(control.value);
      const adjustedInputDate = new Date(
        inputDate.getTime() + inputDate.getTimezoneOffset() * 60 * 1000
      );
      const today = new Date();

      today.setHours(0, 0, 0, 0);

      if (adjustedInputDate < today) {
        return { pastDate: true };
      }
      return null;
    };
  }

  private checkIfDateIsToday(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const inputDateRaw = control.value;
      if (inputDateRaw == null) {
        return null;
      }

      const [year, month, day] = inputDateRaw
        .split('-')
        .map((date) => parseInt(date, 10));
      const inputDate = new Date(year, month - 1, day);

      const normalize = (d: Date) => {
        const n = new Date(d);
        n.setHours(0, 0, 0, 0);
        return n;
      };

      const normalizedInput = normalize(inputDate);
      const normalizedToday = normalize(new Date());

      if (normalizedInput.getTime() === normalizedToday.getTime()) {
        return { isToday: true };
      }
      return null;
    };
  }

  private checkSignupDate(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null || this.form.value.startDate == null) {
        return null;
      }
      const signupDate = new Date(control.value);
      const startDate = new Date(this.form.value.startDate);

      if (signupDate >= startDate) {
        return { afterStart: true };
      }
      return null;
    };
  }
  private checkIfRequire(validateFn: any): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (validateFn()) {
        return null;
      }

      if (control.value == null || control.value === '') {
        return { required: true };
      }
      return null;
    };
  }

  private calculateEndDate(startDate: Date): Date {
    const start = new Date(startDate);
    start.setDate(start.getDate() + this.challenge().durationInDays);
    return start;
  }

  private createChallenge(): void {
    const tenantId = this.tenantService.getTenantId();
    if (!tenantId) {
      this.logger.error('Missing tenant for the user!');
      const message = this.translatorService.translate(
        'challenges.challengeCouldNotBeSaved'
      );
      this.sendToastMessage(message, ToastType.ERROR);
      this.isSaving.set(false);
      return;
    }
    const existingChallenge = this.challenge();

    const newChallenge: ChallengeBody = {
      isShareable: false,
      isLive: true,
      title: this.form.value.title,
      description: this.form.value.description,
      name: existingChallenge.name + new Date().toISOString(),
      accessControlType: 'enterprise_group',
      accessControlKey: tenantId,
      dynamicCategoryObjectId: existingChallenge.dynamicCategoryObjectId,
      durationInDays: existingChallenge.durationInDays,
      workoutType: existingChallenge.workoutType,
      minDaysPerWeek: existingChallenge.minDaysPerWeek,
      imageUrl: existingChallenge.imageUrl,
      bucketSize: existingChallenge.bucketSize,
      cardImageUrl: existingChallenge.cardImageUrl,
      badgeImageUrl: existingChallenge.badgeImageUrl,
      cutoutImageUrl: existingChallenge.cutoutImageUrl,
      teamAssignmentStrategy: existingChallenge.teamAssignmentStrategy,
      teamAssignmentStrategyValue:
        existingChallenge.teamAssignmentStrategyValue,
      allowAutoJoin: this.form.value.allowAutoJoin,
      primaryColor: existingChallenge.primaryColor,
      signupDate: new Date(this.form.value.signupDate),
      startDate: new Date(this.form.value.startDate),
    };

    if (
      this.teamAssignmentStrategy() ===
      GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_COUNT
    ) {
      newChallenge.teamAssignmentStrategy =
        GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_COUNT;
      newChallenge.teamAssignmentStrategyValue = Number(
        this.form.value.numberOfTeams
      ).toString();
    } else if (
      this.teamAssignmentStrategy() ===
      GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_SIZE
    ) {
      newChallenge.teamAssignmentStrategy =
        GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_SIZE;
      newChallenge.teamAssignmentStrategyValue = Number(
        this.form.value.maxTeamNumber
      ).toString();
    } else if (
      this.teamAssignmentStrategy() ===
      GroupChallengeTeamAssignmentStrategy.MANUAL
    ) {
      newChallenge.teamAssignmentStrategy =
        GroupChallengeTeamAssignmentStrategy.MANUAL;
      newChallenge.teamAssignmentStrategyValue = null;
      newChallenge.teams = existingChallenge.teams;
      newChallenge.allowAutoJoin = false;
      newChallenge.signupDate = null;
    } else {
      newChallenge.teamAssignmentStrategy =
        GroupChallengeTeamAssignmentStrategy.DEFAULT;
      newChallenge.teamAssignmentStrategyValue = null;
    }

    this.isSaving.set(true);
    this.challengesServicev2.createChallenge(newChallenge).subscribe({
      next: (created) => {
        this.isSaving.set(false);
        this.saveChanges.setHasChanges(false);
        const message = this.translatorService.translate(
          'challenges.challengeSaved'
        );
        this.sendToastMessage(message, ToastType.INFO);
        this.router.navigate(
          [
            'challenges',
            'edit',
            this.challengesService.encodeChallengeId(created.groupChallengeId),
          ],
          {
            queryParams: {
              from: GroupChallengeRoute.challenges,
            },
          }
        );
      },
      error: (e) => {
        this.logger.error(`Error creating challenge: ${JSON.stringify(e)}`);
        const message =
          e.error.errors[0] ??
          this.translatorService.translate(
            'challenges.challengeCouldNotBeSaved'
          );
        this.sendToastMessage(message, ToastType.ERROR);
        this.isSaving.set(false);
      },
    });
  }

  private updateChallenge(): void {
    const updateChallenge = this.hasChallengeChanges()
      ? this.challengesServicev2.updateChallenge(
          this.challenge().groupChallengeId,
          {
            ...this.challenge(),
            title: this.form.value.title,
            description: this.form.value.description,
            teamAssignmentStrategy: this.teamAssignmentStrategy(),
            teamAssignmentStrategyValue:
              this.teamAssignmentStrategy() ===
              GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_COUNT
                ? Number(this.form.value.numberOfTeams).toString()
                : this.teamAssignmentStrategy() ===
                  GroupChallengeTeamAssignmentStrategy.FIXED_TEAM_SIZE
                ? Number(this.form.value.maxTeamNumber).toString()
                : null,
            allowAutoJoin:
              this.teamAssignmentStrategy() ===
              GroupChallengeTeamAssignmentStrategy.MANUAL
                ? false
                : this.form.value.allowAutoJoin,
            startDate: new Date(this.form.value.startDate),
            signupDate:
              this.teamAssignmentStrategy() ===
              GroupChallengeTeamAssignmentStrategy.MANUAL
                ? null
                : new Date(this.form.value.signupDate),
          }
        )
      : of(this.challenge());

    this.isSaving.set(true);

    updateChallenge.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (update) => {
        this.challenge.set(update);
        this.isSaving.set(false);
        this.readOnlyMode = true;
        this.saveChanges.setHasChanges(false);
        const message = this.translatorService.translate(
          'challenges.challengeSaved'
        );
        this.sendToastMessage(message, ToastType.INFO);
      },
      error: (e) => {
        const message =
          e.error.errors[0] ??
          this.translatorService.translate(
            'challenges.challengeCouldNotBeSaved'
          );
        this.sendToastMessage(message, ToastType.ERROR);
        this.isSaving.set(false);
      },
    });
  }

  private hasChallengeChanges(): boolean {
    if (this.mode() === CreateMode.CREATE) {
      return (
        !!this.form?.value?.title ||
        !!this.form?.value?.description ||
        !!this.form?.value?.allowAutoJoin ||
        !!this.form?.value?.signupDate ||
        !!this.form?.value?.startDate ||
        this.teamChanged
      );
    }

    const start = this.challenge()?.startDate;
    const signup = this.challenge()?.signupDate;

    return (
      this.teamChanged ||
      this.form?.value?.title !== this.challenge()?.title ||
      this.form?.value?.description !== this.challenge()?.description ||
      this.form?.value?.allowAutoJoin !== this.challenge()?.allowAutoJoin ||
      new Date(this.form.value.signupDate).getTime() !==
        new Date(signup).getTime() ||
      new Date(this.form.value.startDate).getTime() !==
        new Date(start).getTime()
    );
  }

  private teamChanged = false;

  onTeamsChanged(teams: ChallengeBodyTeam[]): void {
    this.challenge.update((c) => {
      c.teams = teams;
      return c;
    });
    this.teamChanged = true;
  }

  private setBackTitle(from: GroupChallengeRoute): void {
    let backTitleKey = 'navigation.challengeDetails';
    switch (from) {
      case GroupChallengeRoute.challenges:
        backTitleKey = 'navigation.groupChallenges';
        break;
    }
    this.backTitle.set(backTitleKey);
  }

  private updateStartDateErrors(): void {
    if (this.mode() === CreateMode.EDIT && this.readOnlyMode) return;
    const startDateErrors = this.form.controls.startDate?.errors;
    if (startDateErrors?.required) {
      this.requiredErrorSetter('startDate');
    } else if (startDateErrors?.pastDate) {
      this.errorMessages.update((value) => ({
        ...value,
        startDate: this.translatorService.translate(
          'challenges.validateStartInPast'
        ),
      }));
    } else if (startDateErrors?.isToday) {
      this.errorMessages.update((value) => ({
        ...value,
        startDate: this.translatorService.translate(
          'challenges.validateStartIsToday'
        ),
      }));
    } else {
      this.errorMessages.set({
        ...this.errorMessages(),
        startDate: null,
      });
    }
  }

  private updateSignupDateErrors(): void {
    if (this.mode() === CreateMode.EDIT && this.readOnlyMode) return;
    const signupDateErrors = this.form.controls.signupDate?.errors;
    if (signupDateErrors?.required) {
      this.requiredErrorSetter('signupDate');
    } else if (signupDateErrors?.pastDate) {
      this.errorMessages.update((value) => ({
        ...value,
        signupDate: this.translatorService.translate(
          'challenges.validateSignupInPast'
        ),
      }));
    } else if (signupDateErrors?.afterStart) {
      this.errorMessages.update((value) => ({
        ...value,
        signupDate: this.translatorService.translate(
          'challenges.validateSignupLessStart'
        ),
      }));
    } else {
      this.errorMessages.set({
        ...this.errorMessages(),
        signupDate: null,
      });
    }
  }

  private updateTitleErrors(): void {
    if (this.mode() === CreateMode.EDIT && this.readOnlyMode) return;
    if (this.form.controls.title.errors) {
      this.requiredErrorSetter('title');
    } else {
      this.errorMessages.set({
        ...this.errorMessages(),
        title: null,
      });
    }
  }

  private updateDescriptionErrors(): void {
    if (this.mode() === CreateMode.EDIT && this.readOnlyMode) return;
    if (this.form.controls.description.errors) {
      this.requiredErrorSetter('description');
    } else {
      this.errorMessages.set({
        ...this.errorMessages(),
        description: null,
      });
    }
  }

  private toDateString(date?: Date | string): string {
    return new Date(date).toISOString()?.split('T')[0];
  }

  onSelect(value: GroupChallengeTeamAssignmentStrategy) {
    this.teamAssignmentStrategy.set(value);
    this.teamChanged = true;

    const signupDateControl = this.form.get('signupDate');

    if (value === GroupChallengeTeamAssignmentStrategy.MANUAL) {
      if (!this.challenge().teams?.length) {
        this.challenge.update((c) => {
          c.teams = [];
          this.teamsService.addTeam(c.teams);
          this.teamsService.addTeam(c.teams);
          return c;
        });
      }
      signupDateControl.clearValidators();
      signupDateControl.setValue(null, { emitEvent: false });
      signupDateControl.markAsPristine();
      signupDateControl.markAsUntouched();
    } else {
      signupDateControl.setValidators([
        this.checkIfDateIsPast(),
        this.checkSignupDate(),
        Validators.required,
      ]);
    }
  }
  private updateNumberOfTeamsErrors(): void {
    if (this.mode() === CreateMode.EDIT && this.readOnlyMode) return;

    const control = this.form.controls.numberOfTeams;
    if (control.errors) {
      if (control.errors.required) {
        this.requiredErrorSetter('numberOfTeams');
      } else if (control.errors.min) {
        this.errorMessages.set({
          ...this.errorMessages(),
          numberOfTeams: `Number of teams must be at least ${control.errors.min.min}.`,
        });
      } else if (control.errors.max) {
        this.errorMessages.set({
          ...this.errorMessages(),
          numberOfTeams: `Number of teams must be less than ${
            control.errors.max.max + 1
          }.`,
        });
      }
    } else {
      this.errorMessages.set({
        ...this.errorMessages(),
        numberOfTeams: null,
      });
    }
  }

  private updateMaxNumberOfTeamsErrors(): void {
    if (this.mode() === CreateMode.EDIT && this.readOnlyMode) return;
    const control = this.form.controls.maxTeamNumber;
    if (control.errors) {
      if (control.errors.required) {
        this.requiredErrorSetter('maxTeamNumber');
      } else if (control.errors.min) {
        this.errorMessages.set({
          ...this.errorMessages(),
          maxTeamNumber: `Number of users must be at least ${control.errors.min.min}.`,
        });
      }
    } else {
      this.errorMessages.set({
        ...this.errorMessages(),
        maxTeamNumber: null,
      });
    }
  }
}
